import { Avatar, Button, Divider, IconButton, Stack, Typography } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { ReactComponent as XIcon } from '../../../assets/icons/chat-icons/x.svg';
import { ReactComponent as RobotIcon } from '../../../assets/icons/chat-icons/robot.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/chat-icons/plus.svg';
import CustomButton from '../../../components/button/CustomButton';
import { useAiChatStore } from '../useAiChatStore';
import axios from '../../../api/axios';
import { useEffect, useState } from 'react';
import useAuth from '../../../utils/hooks/useAuth';

/**
 * AI Assistant Conversation History Sidebar
 * Shows list of past conversations with create/delete capabilities
 */
export default () => {
  let [searchParams, setSearchParams] = useSearchParams();

  const {
    conversations,
    activeConversationId,
    setActiveConversation,
    createAiConversation,
    deleteConversation,
    setConversations,
  } = useAiChatStore();
  const [loading, setLoading] = useState(conversations.length === 0);
  const {
    authData: { userData },
  } = useAuth();

  useEffect(() => {
    if (!userData?.id) return;

    const loadSessionsAndMessages = async () => {
      setLoading(true);
      try {
        const sessionsResp = await axios.get('/ai/sessions', {
          headers: {
            'Content-Type': 'application/json',
            'X-User-ID': String(userData.id),
          },
        });

        const sessions = sessionsResp.data ?? [];

        const sessionsWithMessages = await Promise.all(
          sessions.map(async (s: any) => {
            try {
              const msgsResp = await axios.get(
                `/ai/sessions/${s.id}/messages`,
                {
                  headers: {
                    'Content-Type': 'application/json',
                    'X-User-ID': String(userData.id),
                  },
                },
              );

              const msgs = (msgsResp.data ?? [])
                .map((m: any) => ({
                  id: String(m.id),
                  role: (m.role ?? 'assistant') as 'assistant' | 'user',
                  content: m.content ?? '',
                  timestamp: m.created_at ? new Date(m.created_at) : new Date(),
                }))
                .sort((a: any, b: any) => a.timestamp.getTime() - b.timestamp.getTime());

              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: msgs,
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at
                  ? new Date(s.updated_at)
                  : s.created_at
                  ? new Date(s.created_at)
                  : new Date(),
              };
            } catch (err) {
              console.error(`Failed to load messages for session ${s.id}:`, err);
              return {
                id: String(s.id),
                title: s.title ?? 'AI Conversation',
                messages: [],
                createdAt: s.created_at ? new Date(s.created_at) : new Date(),
                updatedAt: s.updated_at ? new Date(s.updated_at) : new Date(),
              };
            }
          }),
        );

        setConversations(sessionsWithMessages);

        // If nothing is selected, focus the most recent one
        if (!activeConversationId && sessionsWithMessages.length > 0) {
          const latest = [...sessionsWithMessages].sort(
            (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
          )[0];
          setActiveConversation(latest.id);
        }
      } catch (err) {
        console.error('Failed to load AI sessions:', err);
      } finally {
        setLoading(false);
      }
    };

    loadSessionsAndMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.id]);

  const onClose = () => {
    searchParams.delete('showAiAssistant');
    setSearchParams(searchParams);
    setActiveConversation(null);
  };

  return (
    <Stack height="100%" overflow={'hidden'}>
      <Stack
        px={3}
        py={1}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexShrink={0}
      >
        <Typography variant="heading4" color="primaryDark.600">
          AI Assistant
        </Typography>
        <IconButton disableRipple onClick={onClose} sx={{ p: 0 }}>
          <XIcon />
        </IconButton>
      </Stack>

      <Divider color="secondaryBlue.100" />

      {/* after the Divider */}
      <Stack
        sx={{
          flex: 1,
          overflowY: 'auto',
          minHeight: 0,
          overscrollBehavior: 'contain',
        }}
      >
        {/* Sticky header with New Conversation */}
        <Stack
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'primaryDark.150',
            borderBottom: '1px solid',
            borderColor: 'secondaryBlue.100',
            pb: 1,
            pt: 2,
            px: 3,
          }}
        >
          <CustomButton
            variant="primaryText"
            disableRipple
            onClick={async () => {
              await createAiConversation();
            }}
            sx={{ justifyContent: 'flex-start', px: 0 }}
          >
            <Stack direction="row" gap={1} alignItems="center">
              <PlusIcon width={20} height={20} />
              <Typography variant="heading4" color="primaryDark.600">
                New Conversation
              </Typography>
            </Stack>
          </CustomButton>
        </Stack>

        {/* Content directly under the sticky header */}
        {loading ? (
          <Stack py={3} px={3}>
            <Typography variant="body3" color="primaryDark.400">
              Loading chats…
            </Typography>
          </Stack>
        ) : conversations.length === 0 ? (
          <Stack py={4} px={3} alignItems="center">
            <Typography variant="body3" color="primaryDark.400" textAlign="center">
              No conversations yet.
              <br />
              Start a new conversation to get help from AI Assistant.
            </Typography>
          </Stack>
        ) : (
          conversations.map((conversation) => (
            <Stack key={conversation.id} direction="column">
              <Stack direction="row">
                <Button
                  onClick={() => setActiveConversation(conversation.id)}
                  fullWidth
                  sx={{
                    height: '48px',
                    px: 3,
                    py: 1.5,
                    backgroundColor:
                      activeConversationId === conversation.id
                        ? 'secondaryBlue.100'
                        : 'transparent',
                    borderRadius: 0,
                    justifyContent: 'flex-start',
                    '&:hover': { backgroundColor: 'secondaryBlue.50' },
                  }}
                  startIcon={
                    <Avatar sx={{ width: 24, height: 24, backgroundColor: 'secondaryBlue.500' }}>
                      <RobotIcon width={14} height={14} fill="#fff" />
                    </Avatar>
                  }
                >
                  <Stack
                    direction="row"
                    flex={1}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant={activeConversationId === conversation.id ? 'bodySemiBold3' : 'body3'}
                      color="primaryDark.600"
                      noWrap
                      flex={1}
                      textAlign={'left'}
                    >
                      {conversation.title}
                    </Typography>
                    <IconButton
                      component="span"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteConversation(conversation.id);
                      }}
                      sx={{
                        color: 'primaryDark.400',
                        p: 0.5,
                        '&:hover': { backgroundColor: 'primaryDark.100' },
                      }}
                    >
                      <XIcon width={14} height={14} />
                    </IconButton>
                  </Stack>
                </Button>
              </Stack>
              <Divider color="secondaryBlue.100" />
            </Stack>
          ))
        )}
      </Stack>
    </Stack>
  );
};
